/**
 * AR content catalog — aggregator.
 *
 * Per-feature copy lives in feature-owned `i18n/` modules (e.g.
 * `features/landing/i18n/hero.ar.ts`). The root file spreads them into the
 * flat dot-keyed shape consumed by `t()`. Keys not present in this catalog
 * (or present with an empty string) fall back to EN via `translate()`.
 *
 * **Arabic source order:** values are stored in **logical (typed) order**.
 * Browser RTL rendering is driven by `<html dir="rtl">` — do not manually
 * reverse strings.
 */

import { landingAr, landingPlaceholdersAr } from '@/features/landing/i18n';
import { authAr } from '@/features/identity-management/auth/i18n';
import { earlyAccessAr } from '@/features/early-access/i18n';
import type { Messages } from './en';

/** App-level AR copy. */
const appLevel = {
  'app.name': 'ووركرايز',
  'meta.title': 'ووركرايز | ذكاء المواهب',
  'app.tagline': '',
  'common.loading': 'جارٍ التحميل…',
  'common.close': 'إغلاق',
  'common.save': 'حفظ',
  'common.cancel': 'إلغاء',
  'common.create': 'إنشاء',
  'common.edit': 'تعديل',
  'common.view': 'عرض',
  'common.delete': 'حذف',
  'common.add': 'إضافة',
  'common.remove': 'إزالة',
  'common.done': 'تم',
  'password.req.title': 'يجب أن تتضمن كلمة المرور:',
  'password.req.length': '10 أحرف على الأقل',
  'password.req.lowercase': 'حرف صغير',
  'password.req.uppercase': 'حرف كبير',
  'password.req.number': 'رقم',
  'password.req.match': 'تطابق كلمتا المرور',
  'password.req.met': 'مستوفى',
  'table.search': 'بحث…',
  'table.columns': 'الأعمدة',
  'table.index': '#',
  'table.filters': 'عوامل التصفية',
  'table.filters.title': 'عوامل التصفية',
  'table.filters.subtitle': 'إظهار الصفوف التي تطابق شروطك فقط.',
  'table.filters.match': 'طابِق',
  'table.filters.all': 'كل',
  'table.filters.any': 'أي',
  'table.filters.ofConditions': 'من الشروط',
  'table.filters.field': 'الحقل',
  'table.filters.operator': 'العامل',
  'table.filters.value': 'القيمة',
  'table.filters.add': 'إضافة شرط',
  'table.filters.removeCondition': 'إزالة الشرط',
  'table.filters.apply': 'تطبيق عوامل التصفية',
  'table.filters.clear': 'مسح الكل',
  'table.columns.title': 'الأعمدة',
  'table.columns.subtitle': 'اختر الأعمدة التي تريد إظهارها.',
  'table.columns.reorderHint': 'اسحب لإعادة الترتيب أو استخدم الأسهم. بدّل للإظهار أو الإخفاء.',
  'table.columns.dragHandle': 'اسحب لإعادة ترتيب العمود',
  'table.columns.moveUp': 'تحريك لأعلى',
  'table.columns.moveDown': 'تحريك لأسفل',
  'table.value.true': 'نعم',
  'table.value.false': 'لا',
  'table.op.eq': 'يساوي',
  'table.op.ne': 'لا يساوي',
  'table.op.gt': 'أكبر من',
  'table.op.lt': 'أصغر من',
  'table.op.gte': 'أكبر أو يساوي',
  'table.op.lte': 'أصغر أو يساوي',
  'table.op.contains': 'يحتوي',
  'table.op.startsWith': 'يبدأ بـ',
  'table.op.endsWith': 'ينتهي بـ',
  'table.filter.any': 'الكل',
  'table.filter.remove': 'إزالة عامل التصفية',

  // Invitations
  'invite.action': 'دعوة مستخدم',
  'invite.title': 'دعوة مستخدم',
  'invite.subtitle': 'أرسل دعوة بالبريد مع تحديد الدور ورصيد الذكاء الاصطناعي.',
  'invite.email': 'البريد الإلكتروني',
  'invite.roles': 'الدور',
  'invite.roles.empty': 'لا توجد أدوار متاحة.',
  'invite.plan': 'الخطة',
  'invite.plan.empty': 'لا توجد خطط مفعّلة. أنشئ خطة من صفحة الخطط أولًا.',
  'invite.plan.creditsSuffix': 'رصيد',
  'invite.credits': 'رصيد الذكاء الاصطناعي',
  'invite.credits.unlimited': 'رصيد غير محدود',
  'invite.credits.placeholder': 'المقدار (اختياري)',
  'invite.submit': 'إرسال الدعوة',
  'invite.sent.title': 'تم إرسال الدعوة',
  'invite.sent.body': 'أرسلنا دعوة إلى',
  'invite.error.title': 'تعذّر إرسال الدعوة',
  'invite.error.body': 'حدث خطأ ما. حاول مرة أخرى.',
  'inviteAccept.title': 'مرحباً بك في Workerize',
  'inviteAccept.subtitle': 'أنت بصدد إعداد الحساب الخاص بـ',
  'inviteAccept.firstName': 'الاسم الأول',
  'inviteAccept.lastName': 'اسم العائلة',
  'inviteAccept.submit': 'إنشاء حسابي',
  'inviteAccept.submitting': 'جارٍ الإنشاء…',
  'inviteAccept.invalid.body':
    'رابط الدعوة هذا غير صالح أو منتهي الصلاحية أو تم استخدامه مسبقاً.',
  'inviteAccept.goToLogin': 'الذهاب إلى تسجيل الدخول',
  'inviteAccept.done.title': 'تم إنشاء الحساب 🎉',
  'inviteAccept.done.body':
    'تحقق من بريدك للحصول على كلمة المرور المؤقتة ورابط تسجيل الدخول.',
  'inviteAccept.error.body': 'تعذّر إنشاء حسابك. حاول مرة أخرى.',
  'forcePw.title': 'تعيين كلمة مرور جديدة',
  'forcePw.subtitle':
    'تم إنشاء حسابك بكلمة مرور مؤقتة. اختر كلمة مرور جديدة للمتابعة.',
  'forcePw.current': 'كلمة المرور المؤقتة',
  'forcePw.new': 'كلمة المرور الجديدة',
  'forcePw.confirm': 'تأكيد كلمة المرور الجديدة',
  'forcePw.submit': 'تحديث كلمة المرور',
  'forcePw.submitting': 'جارٍ التحديث…',
  'forcePw.mismatch': 'كلمتا المرور غير متطابقتين.',
  'forcePw.success': 'تم تحديث كلمة المرور.',
  'forcePw.error': 'تعذّر تحديث كلمة المرور.',
  'table.actions': 'إجراءات',
  'table.noResults': 'لا توجد نتائج.',
  'table.error': 'تعذّر تحميل البيانات.',
  'table.rowsPerPage': 'صفوف لكل صفحة',
  'table.prev': 'الصفحة السابقة',
  'table.next': 'الصفحة التالية',
} as const;

/** Foundation chrome — empty strings fall back to EN via translate(). */
const chrome = {
  'landing.placeholder.title': '',
  'landing.placeholder.body': '',

  'theme.toggle.label': '',
  'theme.toggle.light': '',
  'theme.toggle.dark': '',
  'theme.toggle.to-light.label': 'التبديل إلى الوضع الفاتح',
  'theme.toggle.to-dark.label': 'التبديل إلى الوضع الداكن',

  'locale.toggle.label': '',
  'locale.toggle.en': '',
  'locale.toggle.ar': '',
  'locale.toggle.aria.label': 'تغيير اللغة',
  'locale.short.en': 'EN',
  'locale.short.ar': 'AR',

  'nav.public.label': '',
  'nav.public.platform': '',
  'nav.public.graphs': '',
  'nav.public.candidates': '',
  'nav.public.employers': '',

  'footer.tagline': '',
  'footer.region': '',
  'footer.col.platform.label': '',
  'footer.col.platform.heading': '',
  'footer.col.legal.label': '',
  'footer.col.legal.heading': '',
  'footer.col.region.heading': '',
  'footer.legal.privacy': '',
  'footer.legal.terms': '',
  'footer.legal.contact': '',
  'footer.entity.suffix': '',
  'footer.entity.dec054': '',

  'a11y.skip-to-content': '',

  'page-status.placeholder.label': '',
  'page-status.loading.label': '',
  'page-status.error.label': '',
  'page-status.empty.label': '',
  'breadcrumb.label': 'مسار التنقل',
  'breadcrumb.home': 'الرئيسية',
  'breadcrumb.workspace': 'مساحة العمل',
  'breadcrumb.profile': 'الملف المهني',
  'toast.dismiss': 'إغلاق الإشعار',

  'router.not-found.title': 'الصفحة غير موجودة',
  'router.not-found.body': 'الصفحة التي تبحث عنها غير موجودة.',

  'router.error.title': 'حدث خطأ ما',
  'router.error.body':
    'واجهتنا مشكلة غير متوقعة أثناء تحميل هذه الصفحة. حاول مرة أخرى أو عُد إلى الصفحة الرئيسية.',
  'router.error.retry': 'إعادة المحاولة',
  'router.error.home': 'العودة للرئيسية',
  'router.unauthorized.title': 'تم رفض الوصول',
  'router.unauthorized.body': '',
  'router.serviceDown.title': 'الخدمة غير متاحة مؤقتاً',
  'router.serviceDown.body': '',

  'assistant.fab.label': 'افتح مساعد ووركرايز',
  'assistant.title': 'مساعد ووركرايز',
  'assistant.subtitle': 'إرشاد مهني بالذكاء الاصطناعي',
  'assistant.greeting':
    'مرحبًا! أنا مساعدك المهني في ووركرايز. اسألني عن ملفك أو الأدوار المستهدفة أو بحثك عن عمل.',
  'assistant.placeholder': 'اسأل أي شيء…',
  'assistant.thinking': 'جارٍ التفكير…',
  'assistant.disabled':
    'مساعد الذكاء الاصطناعي غير مُفعّل بعد على هذا النظام. أضِف مفتاح Anthropic لتفعيله.',
  'assistant.error': 'حدث خطأ ما. حاول مرة أخرى.',
  'assistant.outOfCredits':
    'لقد نفد رصيد الذكاء الاصطناعي لديك. اطلب من المسؤول إضافة رصيد.',
  'assistant.input.placeholder': 'اسأل أي شيء…',
  'assistant.input.label': 'راسل المساعد',
  'assistant.send': 'إرسال',
  'assistant.close': 'إغلاق المساعد',
  'assistant.reply': 'لا يولّد ووركرايز إرشاد المساعد قبل وجود دليل حقيقي من الملف والسيرة.',
  'assistant.reserved.title': 'المساعد ينتظر إشارات حقيقية',
  'assistant.reserved.body':
    'أنشئ ملفك وارفع سيرة حقيقية أولًا. لا يعرض ووركرايز إرشاد ذكاء اصطناعي محاكى في العرض التجريبي.',
  'assistant.reserved.input': 'إدخال المساعد محجوز إلى أن توجد إشارات حقيقية.',

  'appShell.topbar.title': 'مساحة العمل',
  'appShell.topbar.subtitle': 'ذكاء المواهب وإشارات الملف ومسارات النمو.',
  'appShell.topbar.tab.overview': 'نظرة عامة',
  'appShell.topbar.tab.analytics': 'التحليلات',
  'appShell.topbar.tab.integrations': 'التكاملات',
  'appShell.topbar.search.label': 'بحث سريع',
  'appShell.topbar.search.placeholder': 'بحث…',
  'appShell.topbar.search.empty': 'لا توجد صفحات مطابقة',
  'appShell.search.section.workspace': 'مساحة العمل',
  'appShell.search.section.employer': 'صاحب العمل',
  'appShell.search.section.admin': 'الإدارة',
  'appShell.credits.label': 'رصيد الذكاء الاصطناعي',
  'appShell.credits.unlimited': 'رصيد ذكاء اصطناعي غير محدود',
  'appShell.reserved.analytics.title': 'التحليلات محجوزة',
  'appShell.reserved.analytics.body':
    'ستظهر التحليلات بعد توفر سجل حقيقي من الملف والسيرة والنشاط.',
  'appShell.reserved.integrations.title': 'التكاملات محجوزة',
  'appShell.reserved.integrations.body':
    'التكاملات تنتظر وحدة لاحقة معتمدة. لا تُعرض حالة تكامل وهمية.',
  'appShell.reserved.notifications.title': 'لا توجد إشعارات بعد',
  'appShell.reserved.notifications.body':
    'سيظهر سجل الإشعارات فقط بعد وجود أحداث حقيقية في مساحة العمل.',
  'appShell.reserved.module.title': 'الوحدة محجوزة',
  'appShell.reserved.module.body':
    'هذه الوحدة مخططة لمرحلة لاحقة معتمدة وليست نشطة في العرض التجريبي.',
  'appShell.drawer.open': 'فتح تنقّل مساحة العمل',
  'appShell.drawer.close': 'إغلاق تنقّل مساحة العمل',
  'appShell.notifications.label': 'الإشعارات قادمة قريبًا',
  'appShell.userMenu.open': 'فتح قائمة المستخدم',
  'appShell.userMenu.loading': 'جارٍ تحميل الحساب',
  'appShell.userMenu.nameUnavailable': 'تفاصيل الحساب غير متاحة',
  'appShell.userMenu.emailUnavailable': 'البريد الإلكتروني غير متاح',
  'appShell.sidebar.collapse': 'طي تنقّل مساحة العمل',
  'appShell.sidebar.expand': 'توسيع تنقّل مساحة العمل',
  'appShell.nav.label': 'تنقّل مساحة العمل',
  'appShell.nav.soon': 'قريبًا',
  'appShell.nav.chip.next': 'التالي',
  'appShell.nav.chip.soon': 'قريبًا',
  'appShell.nav.section.workspace': 'مساحة العمل',
  'appShell.nav.section.future': 'وحدات محجوزة',
  'appShell.nav.workspace': 'مساحة العمل',
  'appShell.nav.workspace.description': 'صفحتك الموثّقة داخل ووركرايز.',
  'appShell.nav.profile': 'الملف المهني',
  'appShell.nav.profile.description': 'ذكاء ملفك المهني الحقيقي.',
  'appShell.nav.smartProfile': 'الملف الذكي',
  'appShell.nav.smartProfile.description': 'ذكاء الملف وخريطة إشارات المسار المهني.',
  'appShell.nav.cvIntelligence': 'ذكاء السيرة الذاتية',
  'appShell.nav.cvIntelligence.description': 'فهم السيرة الذاتية وإرشادات تحسينها.',
  'appShell.nav.jobMatches': 'مطابقات الفرص',
  'appShell.nav.jobMatches.description': 'ملاءمة الأدوار والفرص المناسبة.',
  'appShell.nav.applications': 'الطلبات',
  'appShell.nav.applications.description': 'ذاكرة الطلبات وسياق التقدّم.',
  'appShell.nav.company': 'الشركة',
  'appShell.nav.company.description': 'ذكاء المواهب من جهة أصحاب العمل.',
  'appShell.nav.settings': 'الإعدادات',
  'appShell.nav.settings.description': 'تفضيلات مساحة العمل وعناصر التحكم بالحساب.',
  'appShell.nav.earlyAccess': 'الوصول المبكر',
  'appShell.nav.earlyAccess.description': 'مراجعة وإدارة طلبات قائمة الانتظار.',
  'appShell.nav.admin': 'الإدارة',
  'appShell.nav.admin.description': 'إدارة المنصة — المستخدمون والأدوار والإعدادات والمقاييس.',
  'appShell.nav.adminDashboard': 'لوحة التحكم',
  'appShell.nav.adminDashboard.description': 'نظرة حية على مقاييس المنصة.',
  'appShell.nav.adminUsers': 'إدارة المستخدمين',
  'appShell.nav.adminUsers.description': 'إدارة حسابات المستخدمين والحالة وأنواع الفاعلين.',
  'appShell.nav.adminRoles': 'إدارة الأدوار',
  'appShell.nav.adminRoles.description': 'الأدوار وتعيينات الصلاحيات.',
  'appShell.nav.adminPlans': 'إدارة الخطط',
  'appShell.nav.adminPlans.description': 'خطط الاشتراك والاستحقاقات.',
  'appShell.nav.adminAiUsage': 'استخدام الذكاء الاصطناعي',
  'appShell.nav.adminAiUsage.description': 'تحليلات استهلاك الرموز ونداءات الذكاء الاصطناعي.',
  'appShell.nav.adminEarlyAccess': 'إدارة الوصول المبكر',
  'appShell.nav.adminEarlyAccess.description': 'مراجعة وإدارة طلبات قائمة الانتظار.',
  'appShell.nav.adminSettings': 'إعدادات النظام',
  'appShell.nav.adminSettings.description': 'تهيئة المنصة وأعلام الميزات.',
  'appShell.nav.adminAuditLogs': 'سجل التدقيق',
  'appShell.nav.adminAuditLogs.description': 'كل إجراء تم على المنصة.',
  'admin.audit.title': 'سجل التدقيق',
  'admin.audit.col.time': 'الوقت',
  'admin.audit.col.actor': 'المنفِّذ',
  'admin.audit.col.action': 'الإجراء',
  'admin.audit.col.module': 'الوحدة',
  'admin.audit.col.status': 'الحالة',
  'admin.audit.searchPlaceholder': 'ابحث في الإجراء أو الوحدة…',
  'admin.audit.empty': 'لا توجد أحداث تدقيق بعد.',
  'admin.audit.detailTitle': 'حدث التدقيق',
  'admin.audit.changes': 'ما الذي تغيّر',
  'admin.audit.noChanges': 'لم تُسجَّل تغييرات على مستوى الحقول لهذا الحدث.',
  'admin.audit.field': 'الحقل',
  'admin.audit.before': 'قبل',
  'admin.audit.after': 'بعد',
  'admin.audit.entity': 'الكيان',
  'admin.audit.requestId': 'معرّف الطلب',
  'admin.audit.route': 'المسار',

  // ---- Employer modules ----
  'appShell.nav.jobs': 'الوظائف',
  'appShell.nav.jobs.description': '',
  'appShell.nav.companyProfile': 'ملف الشركة',
  'appShell.nav.companyProfile.description': '',
  'appShell.nav.findCandidates': 'البحث عن مرشحين',
  'appShell.nav.findCandidates.description': '',
  'appShell.nav.employerApplications': 'الطلبات',
  'appShell.nav.employerApplications.description': '',

  'appShell.nav.employerDashboard': 'لوحة التحكم',
  'appShell.nav.employerDashboard.description': '',

  // ---- Employer dashboard ----
  'employerDash.title': 'لوحة التحكم',
  'employerDash.subtitle': '',
  'employerDash.metric.jobs': 'الوظائف',
  'employerDash.metric.jobs.hint': '',
  'employerDash.metric.published': 'الوظائف المنشورة',
  'employerDash.metric.published.hint': '',
  'employerDash.metric.applications': 'الطلبات',
  'employerDash.metric.applications.hint': '',
  'employerDash.metric.review': 'قيد المراجعة',
  'employerDash.metric.review.hint': '',
  'employerDash.metric.offers': 'العروض',
  'employerDash.metric.offers.hint': '',
  'employerDash.recent': 'أحدث الطلبات',
  'employerDash.recent.empty': 'لا توجد طلبات بعد.',
  'employerDash.error': 'تعذّر تحميل لوحة التحكم.',

  // ---- Restricted (no-workspace) actors ----
  'appShell.restricted.title': 'لا يوجد وصول إلى مساحة العمل',
  'appShell.restricted.body': '',

  // ---- User menu ----
  'appShell.userMenu.account': 'إعدادات الحساب',
  'appShell.userMenu.logout': 'تسجيل الخروج',

  // ---- Account screen ----
  'account.title': 'الحساب',
  'account.subtitle': '',
  'account.profile.title': 'الملف الشخصي',
  'account.profile.firstName': 'الاسم الأول',
  'account.profile.lastName': 'اسم العائلة',
  'account.profile.email': 'البريد الإلكتروني',
  'account.profile.emailHint': '',
  'account.profile.locale': 'اللغة',
  'account.profile.save': 'حفظ التغييرات',
  'account.profile.saved': '',
  'account.profile.error': '',
  'account.avatar.title': 'صورة الملف الشخصي',
  'account.avatar.upload': 'رفع صورة',
  'account.avatar.remove': 'إزالة',
  'account.avatar.hint': '',
  'account.avatar.saved': '',
  'account.avatar.removed': '',
  'account.avatar.error': '',
  'account.password.title': 'تغيير كلمة المرور',
  'account.password.current': 'كلمة المرور الحالية',
  'account.password.new': 'كلمة المرور الجديدة',
  'account.password.confirm': 'تأكيد كلمة المرور الجديدة',
  'account.password.submit': 'تحديث كلمة المرور',
  'account.password.changed': '',
  'account.password.mismatch': '',
  'account.password.weak': '',
  'account.password.required': 'هذا الحقل مطلوب.',
  'account.password.error': '',
  'account.password.oauthOnly': '',
  'account.privacy.link': 'الخصوصية وإمكانية الاكتشاف',
  'account.privacy.hint': '',

  // ---- Company logo (employer) ----
  'company.logo.title': 'شعار الشركة',
  'company.logo.upload': 'رفع الشعار',
  'company.logo.remove': 'إزالة',
  'company.logo.hint': '',
  'company.logo.saved': '',
  'company.logo.removed': '',
  'company.logo.error': '',

  // ---- Admin OS ----
  'admin.loading': 'جارٍ التحميل…',
  'admin.loadError': 'تعذّر تحميل هذه البيانات.',
  'admin.dashboard.title': 'لوحة الإدارة',
  'admin.dashboard.subtitle': 'مقاييس المنصة المباشرة والإدارة.',
  'admin.dashboard.eyebrow': 'ذكاء المنصة',
  'admin.dashboard.aiActivity': 'نشاط الذكاء الاصطناعي',
  'admin.dashboard.aiAvg': 'متوسط الرموز / نداء',
  'admin.dashboard.aiByFeature': 'أكثر الميزات استهلاكاً للرموز',
  'admin.dashboard.aiSource.langfuse': 'عبر Langfuse',
  'admin.dashboard.aiSource.local': 'بيانات محلية',
  'admin.nav.users': 'المستخدمون',
  'admin.nav.roles': 'الأدوار والصلاحيات',
  'admin.nav.plans': 'الخطط',
  'admin.nav.aiUsage': 'استهلاك الذكاء الاصطناعي',
  'admin.nav.settings': 'إعدادات النظام',
  'admin.nav.earlyAccess': 'الوصول المبكر',
  'admin.kpi.users': 'المستخدمون',
  'admin.kpi.companies': 'الشركات',
  'admin.kpi.jobs': 'الوظائف',
  'admin.kpi.applications': 'الطلبات',
  'admin.kpi.earlyAccess': 'طلبات الوصول المبكر',
  'admin.kpi.cvDocuments': 'مستندات السيرة الذاتية',
  'admin.kpi.aiTokens': 'رموز الذكاء الاصطناعي',
  'admin.kpi.aiCalls': 'استدعاءات الذكاء الاصطناعي',
  'admin.kpi.users.hint': 'إجمالي الحسابات المسجّلة عبر جميع الأنواع.',
  'admin.kpi.companies.hint': 'ملفات الشركات لأصحاب العمل على المنصّة.',
  'admin.kpi.jobs.hint': 'إجمالي الوظائف المنشورة من أصحاب العمل.',
  'admin.kpi.applications.hint': 'إجمالي طلبات المرشحين المقدّمة للوظائف.',
  'admin.kpi.earlyAccess.hint': 'طلبات قائمة الانتظار المستلمة.',
  'admin.kpi.cvDocuments.hint': 'السير الذاتية المرفوعة من المرشحين.',
  'admin.kpi.aiTokens.hint': 'إجمالي رموز الذكاء الاصطناعي المستهلكة.',
  'admin.kpi.aiCalls.hint': 'إجمالي استدعاءات الذكاء الاصطناعي.',
  'admin.breakdown.applicationsByStatus.hint': '',
  'admin.breakdown.usersByStatus.hint': '',
  'admin.breakdown.usersByActor.hint': '',
  'admin.breakdown.jobsByStatus.hint': '',
  'admin.breakdown.earlyAccessByStatus.hint': '',
  'admin.breakdown.usersByActor': 'المستخدمون حسب النوع',
  'admin.breakdown.usersByStatus': 'المستخدمون حسب الحالة',
  'admin.breakdown.jobsByStatus': 'الوظائف حسب الحالة',
  'admin.breakdown.applicationsByStatus': 'الطلبات حسب الحالة',
  'admin.breakdown.earlyAccessByStatus': 'الوصول المبكر حسب الحالة',
  'admin.users.title': 'المستخدمون',
  'admin.users.subtitle': 'إدارة الحسابات والأدوار والأنواع ورصيد الذكاء الاصطناعي.',
  'admin.users.saveError': 'تعذّر حفظ التغيير.',
  'admin.users.col.user': 'المستخدم',
  'admin.users.col.actorType': 'النوع',
  'admin.users.col.status': 'الحالة',
  'admin.users.actorType.none': 'لا شيء',
  'admin.users.manage': 'إدارة',
  'admin.users.close': 'إغلاق',
  'admin.users.roles': 'الأدوار',
  'admin.users.saveRoles': 'حفظ الأدوار',
  'admin.users.credits': 'رصيد الذكاء الاصطناعي',
  'admin.users.unlimited': 'غير محدود',
  'admin.users.grantPlaceholder': 'المقدار',
  'admin.users.grant': 'منح',
  'admin.users.makeUnlimited': 'جعله غير محدود',
  'admin.users.revokeUnlimited': 'إلغاء غير المحدود',
  'admin.users.plan': 'الخطة',
  'admin.users.noPlan': 'بدون خطة',
  'admin.users.applyPlan': 'تطبيق الخطة',
  'admin.users.planApplied': 'تم تطبيق الخطة — تم تحديث الرصيد.',
  'admin.roles.title': 'الأدوار والصلاحيات',
  'admin.roles.subtitle': 'تعديل الصلاحيات التي يمنحها كل دور.',
  'admin.roles.saved': 'تم حفظ الصلاحيات.',
  'admin.roles.saveError': 'تعذّر حفظ الصلاحيات.',
  'admin.roles.system': 'نظامي',
  'admin.roles.permsLabel': 'صلاحية',
  'admin.roles.save': 'حفظ الصلاحيات',
  'admin.aiUsage.title': 'استهلاك الذكاء الاصطناعي',
  'admin.aiUsage.subtitle': 'استهلاك الرموز عبر ميزات الذكاء الاصطناعي.',
  'admin.aiUsage.calls': 'إجمالي الاستدعاءات',
  'admin.aiUsage.inputTokens': 'رموز الإدخال',
  'admin.aiUsage.outputTokens': 'رموز الإخراج',
  'admin.aiUsage.totalTokens': 'إجمالي الرموز',
  'admin.aiUsage.empty': 'لا يوجد استهلاك مُسجّل بعد.',
  'admin.aiUsage.costPerCredit': 'التكلفة / رصيد',
  'admin.aiUsage.estimatedCost': 'تكلفة الذكاء الاصطناعي التقديرية',
  'admin.aiUsage.monthlyRevenue': 'الإيراد الشهري المتكرر',
  'admin.aiUsage.grossMargin': 'هامش الربح الإجمالي التقديري',
  'admin.aiUsage.col.feature': 'الميزة',
  'admin.aiUsage.col.model': 'النموذج',
  'admin.aiUsage.col.tokens': 'الرموز',
  'admin.aiUsage.col.when': 'الوقت',
  'admin.settings.title': 'إعدادات النظام',
  'admin.settings.subtitle': 'قيم الإعداد أثناء التشغيل.',
  'admin.settings.saved': 'تم حفظ الإعداد.',
  'admin.settings.saveError': 'تعذّر حفظ الإعداد.',
  'admin.settings.empty': 'لا توجد إعدادات.',
  'admin.settings.save': 'حفظ',
  'admin.settings.col.key': 'المفتاح',
  'admin.settings.col.type': 'النوع',
  'admin.settings.col.value': 'القيمة',
  'admin.plans.title': 'خطط الاشتراك',
  'admin.plans.subtitle': 'حدّد الخطط ومخصصات رصيد الذكاء الاصطناعي.',
  'admin.plans.created': 'تم إنشاء الخطة.',
  'admin.plans.saveError': 'تعذّر حفظ الخطة.',
  'admin.plans.empty': 'لا توجد خطط بعد — أنشئ واحدة أعلاه.',
  'admin.plans.field.code': 'الرمز',
  'admin.plans.field.name': 'الاسم',
  'admin.plans.field.credits': 'الرصيد الشهري',
  'admin.plans.field.price': 'السعر (دولار)',
  'admin.plans.field.interval': 'الفوترة',
  'admin.plans.field.features': 'المزايا',
  'admin.plans.field.policies': 'السياسات',
  'admin.plans.features.placeholder': 'أضف ميزة…',
  'admin.plans.policies.placeholder': 'أضف سياسة…',
  'admin.plans.perCredit': 'دولار / رصيد',
  'admin.plans.perCredit.na': 'حدد السعر والرصيد لعرض سعر الرصيد.',
  'admin.plans.margin': 'الهامش',
  'admin.plans.margin.na': 'حدد تكلفة لكل رصيد (في الإعدادات) لعرض الهامش.',
  'admin.plans.interval.monthly': 'شهري',
  'admin.plans.interval.yearly': 'سنوي',
  'admin.plans.interval.one_time': 'مرة واحدة',
  'admin.plans.create': 'إنشاء خطة',
  'admin.plans.creditsLabel': 'رصيد',
  'admin.plans.deactivate': 'تعطيل',
  'admin.plans.activate': 'تفعيل',

  // ---- Admin redesign (DataTable + modals) ----
  'admin.users.col.verified': 'موثّق',
  'admin.users.col.roles': 'الأدوار',
  'admin.users.col.created': 'تاريخ الإنشاء',
  'admin.users.col.lastLogin': 'آخر دخول',
  'admin.users.edit': 'تعديل المستخدم',
  'admin.users.view': 'تفاصيل المستخدم',
  'admin.users.firstName': 'الاسم الأول',
  'admin.users.lastName': 'اسم العائلة',
  'admin.users.verified': 'البريد موثّق',
  'admin.users.saved': 'تم تحديث المستخدم.',
  'admin.users.never': 'أبداً',
  'admin.roles.edit': 'تعديل الدور',
  'admin.roles.col.role': 'الدور',
  'admin.roles.col.code': 'الرمز',
  'admin.roles.col.perms': 'الصلاحيات',
  'admin.plans.add': 'خطة جديدة',
  'admin.plans.edit': 'تعديل الخطة',
  'admin.plans.saved': 'تم حفظ الخطة.',
  'admin.plans.field.description': 'الوصف',
  'admin.plans.field.active': 'مفعّلة',
  'admin.plans.col.status': 'الحالة',
  'admin.aiUsage.col.user': 'المستخدم',
  'admin.aiUsage.col.status': 'الحالة',
  'admin.aiUsage.status.ok': 'ناجح',
  'admin.aiUsage.status.fail': 'فشل',
  'admin.settings.edit': 'تعديل الإعداد',

  // ---- Job matches (real) ----
  'jobMatches.title': 'الوظائف المطابقة',
  'jobMatches.subtitle': 'وظائف مطابقة لمهاراتك من الإعلانات المنشورة.',
  'jobMatches.loading': 'جارٍ البحث عن تطابقات…',
  'jobMatches.error': 'تعذّر تحميل التطابقات.',
  'jobMatches.empty': 'لا توجد تطابقات بعد',
  'jobMatches.emptyBody': 'أضف مهارات إلى ملفك، أو تحقق لاحقًا عند نشر وظائف.',
  'jobMatches.matched': 'متوافق',
  'jobMatches.missing': 'ناقص',
  'jobMatches.apply': 'تقديم',
  'jobMatches.appliedShort': 'تم التقديم',
  'jobMatches.applied': 'تم إرسال الطلب',
  'jobMatches.applyError': 'تعذّر التقديم على هذه الوظيفة.',

  // ---- Applications (candidate, real) ----
  'applications.title': 'الطلبات',
  'applications.subtitle': 'تتبّع طلبات التوظيف والعروض.',
  'applications.loading': 'جارٍ التحميل…',
  'applications.error': 'تعذّر تحميل طلباتك.',
  'applications.offers': 'العروض المستلمة',
  'applications.noOffers': 'لا توجد عروض بعد.',
  'applications.accept': 'قبول',
  'applications.decline': 'رفض',
  'applications.mine': 'طلباتي',
  'applications.empty': 'لا توجد طلبات بعد',
  'applications.emptyBody': 'قدّم على وظائف من صفحة الوظائف المطابقة لتظهر هنا.',
  'applications.col.job': 'الوظيفة',
  'applications.col.status': 'الحالة',
  'applications.withdraw': 'سحب',
  'applications.actionError': 'فشل الإجراء. حاول مرة أخرى.',
  'applicationStatus.new': 'جديد',
  'applicationStatus.reviewing': 'قيد المراجعة',
  'applicationStatus.offered': 'تم تقديم عرض',
  'applicationStatus.accepted': 'مقبول',
  'applicationStatus.rejected': 'لم يتم الاختيار',
  'applicationStatus.withdrawn': 'مسحوب',
  'offerStatus.sent': 'بانتظار ردك',
  'offerStatus.accepted': 'مقبول',
  'offerStatus.declined': 'مرفوض',
  'offerStatus.withdrawn': 'مسحوب',

  // ---- Notifications (real) ----
  'notifications.label': 'الإشعارات',
  'notifications.title': 'الإشعارات',
  'notifications.empty': 'لا توجد إشعارات بعد',
  'notifications.markAll': 'تعليم الكل كمقروء',
  'notifications.tab.all': 'الكل',
  'notifications.tab.unread': 'غير المقروءة',
  'notifications.tab.read': 'المقروءة',
  'notifications.seeMore': 'عرض المزيد',
  'notifications.seeLess': 'عرض أقل',
  'notifications.from': 'من',
  'appShell.sidebar.status.label': 'حالة الغلاف',
  'appShell.sidebar.status.title': 'أساس مساحة العمل جاهز',
  'appShell.sidebar.status.body':
    'ستُضاف وحدات المنتج الحية هنا مع صدور أسطح ووركرايز المعتمدة.',

  'appHome.eyebrow': 'مساحة عمل موثّقة',
  'appHome.greeting.named': 'مرحبًا بعودتك،',
  'appHome.greeting.default': 'مرحبًا بعودتك إلى ووركرايز',
  'appHome.intro':
    'هذه نقطة البداية الهادئة لمساحة ذكاء ووركرايز. ستُضاف الوحدات المستقبلية هنا من دون تغيير الغلاف.',
  'appHome.card.smartProfile.title': 'الملف الذكي',
  'appHome.card.smartProfile.body':
    'مساحة مستقبلية لإشارات الملف ونقاط القوة واتجاه المسار المهني.',
  'appHome.card.smartProfile.hint': 'ستظهر إشارات الملف هنا.',
  'appHome.card.cvIntelligence.title': 'ذكاء السيرة الذاتية',
  'appHome.card.cvIntelligence.body':
    'مساحة مستقبلية لفهم السيرة الذاتية بصورة منظّمة وتقديم إرشادات تحسين.',
  'appHome.card.cvIntelligence.hint': 'وحدة تحليل السيرة الذاتية قادمة تاليًا.',
  'appHome.card.jobMatches.title': 'مطابقات الفرص',
  'appHome.card.jobMatches.body':
    'مساحة مستقبلية لفرص واعية بالملاءمة من دون تحويل ووركرايز إلى لوحة وظائف.',
  'appHome.card.jobMatches.hint': 'ستظهر المطابقات الواعية بالملاءمة هنا.',
  'appHome.card.reserved': 'محجوز',
  'appHome.workspaceState.label': 'حالة الأساس',
  'appHome.workspaceState.profile': 'طبقة الملف محجوزة',
  'appHome.workspaceState.cv': 'ذكاء السيرة محجوز',
  'appHome.workspaceState.matches': 'خريطة المطابقة محجوزة',
  'appHome.insight.readiness.title': 'جاهزية الملف',
  'appHome.insight.readiness.body':
    'هذه اللوحة مخصّصة لإرشادات الجاهزية عندما تتوفر وحدة ملف المواهب.',
  'appHome.insight.nextAction.title': 'الإجراء التالي',
  'appHome.insight.nextAction.body':
    'ستستبدل الوحدة المعتمدة التالية هذه اللوحة المحجوزة بإرشاد حقيقي.',
  'appHome.insight.ai.title': 'حالة مساعد الذكاء الاصطناعي',
  'appHome.insight.ai.body':
    'سيحافظ ووركرايز على الإرشاد ضمن السياق عندما تصل إشارات الملف والسيرة الذاتية الحقيقية.',
  'appHome.insight.chip': 'قريبًا',

  'appHome.empty.intro':
    'مساحة عملك جاهزة. ستظهر إشارات الملف والسيرة والذكاء الحقيقية هنا فقط بعد إنشائها.',
  'appHome.empty.status': 'فارغ',
  'appHome.empty.profileReadiness.label': 'ذكاء الملف',
  'appHome.empty.profileReadiness.title': 'أنشئ ملفك لفتح ذكاء الملف.',
  'appHome.empty.profileReadiness.body':
    'لم يستقبل ووركرايز دليل الملف أو السيرة بعد، لذلك تبقى درجات الجاهزية والاتجاهات مخفية إلى أن توجد بيانات حقيقية.',
  'appHome.empty.profileReadiness.step.identity': 'الهوية متصلة',
  'appHome.empty.profileReadiness.step.profile': 'لم يتم إنشاء الملف',
  'appHome.empty.profileReadiness.step.cv': 'لم يتم رفع السيرة',
  'appHome.empty.nextAction.label': 'الإجراء التالي',
  'appHome.empty.nextAction.title': 'ارفع سيرتك الذاتية لبدء تحليلها.',
  'appHome.empty.nextAction.body':
    'ستظهر رؤى السيرة بعد رفع سيرة ذاتية حقيقية وتحليلها. لا تُنشأ توصيات قبل ذلك.',
  'appHome.empty.nextAction.status': 'بانتظار سيرة حقيقية',
  'appHome.empty.nextAction.action': 'انتقل إلى رفع السيرة',
  'appHome.empty.profile.title': 'الملف الذكي',
  'appHome.empty.profile.body':
    'سيظهر ذكاء الملف بعد إنشاء ملف المرشح الخاص بك.',
  'appHome.empty.profile.action': 'أنشئ ملفك المهني',
  'appHome.empty.cv.title': 'ذكاء السيرة الذاتية',
  'appHome.empty.cv.body':
    'ستظهر رؤى السيرة بعد رفع سيرة ذاتية حقيقية وتحليلها.',
  'appHome.empty.cv.action': 'ارفع السيرة أو حلّلها',
  'appHome.empty.applications.title': 'ذكاء الطلبات',
  'appHome.empty.applications.body':
    'لا يوجد ذكاء طلبات بعد. سيعرض ووركرايز سياق الطلبات فقط بعد وجود نشاط حقيقي.',
  'appHome.empty.applications.action': 'تتبع الطلبات محجوز لوحدة لاحقة معتمدة.',
  'appHome.reserved.applications.title': 'ذكاء الطلبات محجوز',
  'appHome.reserved.applications.body':
    'سيعرض ووركرايز سياق الطلبات فقط بعد أن تسجل الوحدة المعتمدة نشاطًا حقيقيًا.',
  'appHome.empty.applicationsPanel.title': 'ذكاء الطلبات',
  'appHome.empty.applicationsPanel.body':
    'هذه المساحة محجوزة لذاكرة الطلبات الحقيقية وسياق التقدم.',
  'appHome.empty.applicationsPanel.emptyTitle': 'لا يوجد ذكاء طلبات بعد.',
  'appHome.empty.applicationsPanel.emptyBody':
    'لا يعرض ووركرايز صفوف طلبات أو درجات مطابقة أو إشارات مراحل وهمية. ستظهر الصفوف الحقيقية بعد أن تسجل الوحدة المناسبة نشاطًا.',
  'appHome.empty.rail.label': 'سياق الذكاء',
  'appHome.empty.rail.intro':
    'سيلخص هذا الجانب إشارات الملف والسيرة ومساحة العمل الحقيقية بعد وجودها.',
  'appHome.empty.rail.assistant.title': 'حالة مساعد الذكاء الاصطناعي',
  'appHome.empty.rail.assistant.body':
    'ينتظر الإرشاد السياقي إشارات ملف وسيرة حقيقية.',
  'appHome.empty.rail.recommendation.title': 'إجراء مقترح',
  'appHome.empty.rail.recommendation.body':
    'لا توجد توصية ذكاء اصطناعي بعد لأنه لم يتم تحليل دليل حقيقي من السيرة أو الملف.',
  'appHome.empty.rail.trend.title': 'اتجاهات الإشارات',
  'appHome.empty.rail.trend.body':
    'لا تظهر نسب أو خطوط اتجاه وهمية قبل وجود سجل حقيقي.',
  'appHome.empty.rail.network.title': 'شبكة الإشارات',
  'appHome.empty.rail.network.body':
    'سترابط إشارات الملف والسيرة والطلبات والنمو هنا بعد شحن الوحدات الحقيقية.',
  'appHome.empty.rail.footer': 'لوحات ذكاء محجوزة',
  'appHome.empty.rail.footerStatus': 'حالة فارغة',

  'candidateProfile.eyebrow': 'ذكاء الملف',
  'candidateProfile.loading.title': 'جارٍ تحميل الملف',
  'candidateProfile.loading.body': 'نقرأ بيانات ملفك المحفوظة في ووركرايز.',
  'candidateProfile.error.title': 'تعذر تحميل الملف',
  'candidateProfile.action.retry': 'إعادة المحاولة',
  'candidateProfile.action.save': 'حفظ الملف',
  'candidateProfile.action.reset': 'إعادة ضبط التغييرات',
  'candidateProfile.action.add': 'إضافة',
  'candidateProfile.action.remove': 'إزالة',
  'candidateProfile.save.saving': 'جارٍ الحفظ',
  'candidateProfile.save.success': 'تم حفظ الملف.',
  'candidateProfile.save.success.body': 'تم حفظ تغييرات ملفك الحقيقية.',
  'candidateProfile.save.error.title': 'تعذر حفظ الملف',
  'candidateProfile.save.error.body': 'راجع الرسالة في أعلى النموذج ثم حاول مرة أخرى.',
  'candidateProfile.validation.error.title': 'راجع تفاصيل الملف',
  'candidateProfile.validation.years':
    'يجب أن تكون سنوات الخبرة رقمًا صحيحًا بين 0 و80.',
  'candidateProfile.empty.displayName': 'اسمك',
  'candidateProfile.empty.headline': 'أضف عنوانًا لملفك.',
  'candidateProfile.empty.state': 'لم يتم إنشاء ملف بعد.',
  'candidateProfile.empty.body':
    'لم يحفظ ووركرايز ملف مرشح بعد. أضف بياناتك الحقيقية واحفظها لإنشائه.',
  'candidateProfile.empty.skills': 'لا توجد مهارات محفوظة بعد.',
  'candidateProfile.empty.experience': 'لا توجد خبرات محفوظة بعد.',
  'candidateProfile.empty.education': 'لا يوجد تعليم محفوظ بعد.',
  'candidateProfile.empty.projects': 'لا توجد مشاريع محفوظة بعد.',
  'candidateProfile.empty.goals': 'لا توجد أهداف محفوظة بعد.',
  'candidateProfile.completeness.label': 'الاكتمال',
  'candidateProfile.completeness.complete': 'كل أقسام الملف المتتبعة تحتوي على بيانات.',
  'candidateProfile.apply.toast.title': 'تم تطبيق اقتراحات السيرة الذاتية',
  'candidateProfile.apply.toast.body':
    'تم ملء الحقول الفارغة وإضافة مهارات جديدة من سيرتك الذاتية. راجعها ثم احفظ للإبقاء عليها.',
  'candidateProfile.apply.none.title': 'لا شيء لتطبيقه',
  'candidateProfile.apply.none.body': 'ملفك يغطي بالفعل اقتراحات هذه السيرة الذاتية.',
  'candidateProfile.nav.label': 'في هذه الصفحة',
  'candidateProfile.checklist.label': 'قائمة الإكمال',
  'candidateProfile.checklist.done': 'مضاف',
  'candidateProfile.checklist.todo': 'لم يُضف بعد',
  'candidateProfile.section.overview': 'العنوان والنظرة العامة',
  'candidateProfile.section.overview.body':
    'تشكّل هذه التفاصيل أساس الملف الذي ستُبنى عليه إشارات السيرة والذكاء لاحقًا.',
  'candidateProfile.section.summary': 'الملخص المهني',
  'candidateProfile.section.summary.body':
    'اجعل الملخص موجزًا ومبنيًا على دليل حقيقي حتى يفهم ووركرايز اتجاهك المهني.',
  'candidateProfile.section.skills': 'المهارات',
  'candidateProfile.section.skills.body':
    'أضف المهارات يدويًا الآن؛ وقد تُثريها وحدات لاحقة معتمدة من دليل سيرة حقيقي.',
  'candidateProfile.section.experience': 'الخبرة',
  'candidateProfile.section.experience.body':
    'تساعد الخبرات ووركرايز على فهم الدليل المهني خلف ملفك.',
  'candidateProfile.section.education': 'التعليم',
  'candidateProfile.section.education.body':
    'أضف تاريخًا تعليميًا حقيقيًا يدعم إشارات مسارك ومهاراتك.',
  'candidateProfile.section.projects': 'المشاريع',
  'candidateProfile.section.projects.body':
    'تسجل المشاريع دليلًا عمليًا من دون اختلاق إنجازات أو توصيات.',
  'candidateProfile.section.goals': 'الأهداف',
  'candidateProfile.section.goals.body':
    'تساعد الأهداف أي إرشاد مستقبلي معتمد على البقاء منسجمًا مع اتجاهك المقصود.',
  'candidateProfile.field.displayName': 'اسم العرض',
  'candidateProfile.field.headline': 'العنوان',
  'candidateProfile.field.currentRole': 'الدور الحالي',
  'candidateProfile.field.location': 'الموقع',
  'candidateProfile.field.preferredLanguage': 'اللغة المفضلة',
  'candidateProfile.field.summary': 'الملخص',
  'candidateProfile.field.careerLevel': 'المستوى المهني',
  'candidateProfile.field.yearsOfExperience': 'سنوات الخبرة',
  'candidateProfile.field.dateOfBirth': 'تاريخ الميلاد',
  'candidateProfile.field.gender': 'الجنس',
  'candidateProfile.option.gender.unspecified': 'أفضّل عدم الإفصاح',
  'candidateProfile.field.skillName': 'اسم المهارة',
  'candidateProfile.field.title': 'المسمى',
  'candidateProfile.field.company': 'الشركة',
  'candidateProfile.field.startDate': 'تاريخ البدء',
  'candidateProfile.field.endDate': 'تاريخ الانتهاء',
  'candidateProfile.field.isCurrent': 'دور حالي',
  'candidateProfile.field.description': 'الوصف',
  'candidateProfile.field.institution': 'المؤسسة',
  'candidateProfile.field.degree': 'الدرجة',
  'candidateProfile.field.fieldOfStudy': 'مجال الدراسة',
  'candidateProfile.field.projectName': 'اسم المشروع',
  'candidateProfile.field.url': 'الرابط',
  'candidateProfile.field.technologies': 'التقنيات',
  'candidateProfile.field.goalTitle': 'عنوان الهدف',
  'candidateProfile.field.targetDate': 'التاريخ المستهدف',
  'candidateProfile.field.status': 'الحالة',
  'candidateProfile.option.level.none': 'بدون مستوى',
  'candidateProfile.option.status.planned': 'مخطط',
  'candidateProfile.option.status.inProgress': 'قيد التنفيذ',
  'candidateProfile.option.status.completed': 'مكتمل',
  'candidateProfile.panel.snapshot': 'لقطة الملف المحفوظ',
  'candidateProfile.panel.save': 'حفظ التغييرات',
  'candidateProfile.panel.save.body':
    'يحفظ هذا الإجراء الملف وفق استجابة الخادم. تبقى الحقول الاختيارية الفارغة فارغة.',

  'candidateCv.eyebrow': 'ذكاء السيرة الذاتية',
  'candidateCv.title': 'رفع السيرة واستخراجها وتحليلها',
  'candidateCv.body':
    'ارفع سيرة حقيقية بصيغة PDF أو DOCX، استخرج نصها، ثم حلّلها مع ملفك المحفوظ.',
  'candidateCv.loading': 'جارٍ تحميل ذكاء السيرة',
  'candidateCv.upload.label': 'رفع السيرة',
  'candidateCv.upload.hint': 'PDF أو DOCX فقط. الحد الأقصى: 10 ميغابايت.',
  'candidateCv.action.upload': 'رفع السيرة',
  'candidateCv.action.replace': 'استبدال السيرة',
  'candidateCv.action.delete': 'حذف السيرة',
  'candidateCv.action.deleting': 'جارٍ الحذف',
  'candidateCv.action.confirmDelete': 'تأكيد حذف السيرة',
  'candidateCv.action.cancelDelete': 'إلغاء',
  'candidateCv.action.extract': 'استخراج نص السيرة',
  'candidateCv.action.extracting': 'جارٍ الاستخراج',
  'candidateCv.action.analyze': 'تحليل السيرة',
  'candidateCv.action.analyzing': 'جارٍ التحليل',
  'candidateCv.empty.title': 'لا توجد سيرة مرفوعة بعد.',
  'candidateCv.empty.body':
    'ارفع سيرة حقيقية لبدء الاستخراج والتحليل المرتبط بملفك.',
  'candidateCv.current.untitled': 'السيرة الحالية',
  'candidateCv.current.words': 'الكلمات',
  'candidateCv.current.characters': 'الأحرف',
  'candidateCv.status.notStarted': 'لم يتم الاستخراج',
  'candidateCv.status.extracting': 'جارٍ الاستخراج',
  'candidateCv.status.extracted': 'تم الاستخراج',
  'candidateCv.status.extractionFailed': 'فشل الاستخراج',
  'candidateCv.error.noFile': 'اختر ملف PDF أو DOCX قبل الرفع.',
  'candidateCv.error.unsupported': 'الملفات المدعومة هي PDF وDOCX فقط.',
  'candidateCv.error.tooLarge': 'يجب ألا يتجاوز ملف السيرة 10 ميغابايت.',
  'candidateCv.toast.upload.success': 'تم رفع السيرة.',
  'candidateCv.toast.upload.error': 'تعذر رفع السيرة',
  'candidateCv.toast.extract.success': 'تم استخراج نص السيرة.',
  'candidateCv.toast.extract.error': 'تعذر استخراج نص السيرة',
  'candidateCv.toast.analyze.success': 'اكتمل تحليل السيرة.',
  'candidateCv.toast.analyze.error': 'تعذر تحليل السيرة',
  'candidateCv.toast.delete.success': 'تم حذف السيرة.',
  'candidateCv.toast.delete.error': 'تعذر حذف السيرة',
  'candidateCv.delete.confirm.title': 'هل تريد حذف هذه السيرة؟',
  'candidateCv.delete.confirm.body':
    'سيزيل هذا بيانات السيرة المرفوعة وحالة التحليل الظاهرة في ملفك.',
  'candidateCv.analysis.empty.title': 'لا يوجد تحليل بعد.',
  'candidateCv.analysis.empty.body':
    'استخرج نص السيرة أولًا، ثم شغّل التحليل لرؤية نتائج الخادم الحقيقية.',
  'candidateCv.analysis.title': 'نتيجة التحليل',
  'candidateCv.analysis.mode.ai': 'ذكاء اصطناعي',
  'candidateCv.analysis.mode.ruleBased': 'قائم على القواعد',
  'candidateCv.analysis.mode.fallback': 'وضع احتياطي',
  'candidateCv.analysis.fallbackNotice':
    'اكتمل التحليل باستخدام الوضع الاحتياطي.',
  'candidateCv.analysis.ats': 'جاهزية ATS',
  'candidateCv.analysis.profileStrength': 'قوة الملف',
  'candidateCv.analysis.strengths': 'نقاط القوة',
  'candidateCv.analysis.weaknesses': 'نقاط التحسين',
  'candidateCv.analysis.missingSections': 'الأقسام الناقصة',
  'candidateCv.analysis.detectedSkills': 'المهارات المكتشفة',
  'candidateCv.analysis.suggestedSkills': 'المهارات المقترحة',
  'candidateCv.analysis.recommendations': 'التوصيات',
  'candidateCv.analysis.provider.none': 'لم يُستخدم مزود ذكاء اصطناعي خارجي',
  'candidateCv.priority.high': 'عالٍ',
  'candidateCv.priority.medium': 'متوسط',
  'candidateCv.priority.low': 'منخفض',
  'candidateCv.apply.action': 'تطبيق على الملف',
  'candidateCv.apply.hint': 'يملأ الحقول الفارغة في الملف ويضيف مهارات جديدة لمراجعتك.',

  // ── Workspace command center (real-data dashboard rethink) ──
  'appHome.command.readiness.label': 'جاهزية الملف',
  'appHome.command.readiness.suffix': 'مكتمل',
  'appHome.command.readiness.empty': '',
  'appHome.command.readiness.loading': '',
  'appHome.command.nextAction.label': 'أفضل خطوة تالية',
  'appHome.command.action.build.body': '',
  'appHome.command.action.build.cta': 'أنشئ ملفك',
  'appHome.command.action.continue.body': '',
  'appHome.command.action.continue.cta': 'أكمل ملفك',
  'appHome.command.action.explore.body': '',
  'appHome.command.action.explore.cta': '',
  'appHome.command.intel.label': 'ذكاؤك',
  'appHome.command.intel.intro': '',
  'appHome.command.card.live': 'مباشر',
  'appHome.command.card.preview': 'معاينة',
  'appHome.command.card.cta.live': 'فتح',
  'appHome.command.card.cta.preview': 'عرض المعاينة',

  // ── Feature preview surfaces (reserved PRD modules made navigable) ──
  'featurePreview.chip.preview': 'معاينة',
  'featurePreview.sample.badge': 'بيانات تجريبية',
  'featurePreview.sample.note': 'لأغراض التوضيح فقط — ليست بياناتك الحقيقية.',
  'featurePreview.whatItDoes': 'ما الذي ستقدمه هذه الميزة',

  // Job Matches — PRD Module 2 (AI Matching Engine)
  'preview.jobMatches.eyebrow': 'الوحدة 2 · محرك المطابقة بالذكاء الاصطناعي',
  'preview.jobMatches.title': 'فرص العمل المطابقة',
  'preview.jobMatches.body': '',
  'preview.jobMatches.bullet.1': '',
  'preview.jobMatches.bullet.2': '',
  'preview.jobMatches.bullet.3': '',
  'preview.jobMatches.bullet.4': '',
  'preview.jobMatches.phase': '',
  'preview.jobMatches.sample.role': '',
  'preview.jobMatches.sample.company': '',
  'preview.jobMatches.sample.fit': '',
  'preview.jobMatches.sample.reason': '',

  // Applications — PRD Module 3 (Hiring OS, candidate side)
  'preview.applications.eyebrow': 'الوحدة 3 · نظام التوظيف',
  'preview.applications.title': 'الطلبات',
  'preview.applications.body': '',
  'preview.applications.bullet.1': '',
  'preview.applications.bullet.2': '',
  'preview.applications.bullet.3': '',
  'preview.applications.bullet.4': '',
  'preview.applications.phase': '',
  'preview.applications.sample.role': '',
  'preview.applications.sample.stage': '',
  'preview.applications.sample.role2': '',
  'preview.applications.sample.stage2': '',

  // Smart Profile — PRD Module 1 + 4
  'preview.smartProfile.eyebrow': 'الوحدة 1 · هوية المواهب',
  'preview.smartProfile.title': 'الملف الذكي',
  'preview.smartProfile.body': '',
  'preview.smartProfile.bullet.1': '',
  'preview.smartProfile.bullet.2': '',
  'preview.smartProfile.bullet.3': '',
  'preview.smartProfile.bullet.4': '',
  'preview.smartProfile.phase': '',
  'preview.smartProfile.sample.score': '',
  'preview.smartProfile.sample.value': '',
  'preview.smartProfile.sample.factor1': '',
  'preview.smartProfile.sample.factor2': '',
  'preview.smartProfile.sample.factor3': '',

  // Smart Profile — real-data view
  'smartProfile.loading': 'يتم تحميل ملفك الذكي…',
  'smartProfile.empty.title': 'أنشئ ملفك لفتح الملف الذكي',
  'smartProfile.empty.body': '',
  'smartProfile.empty.cta': 'أنشئ ملفك',
  'smartProfile.score.strength': 'قوة الملف',
  'smartProfile.score.completeness': 'اكتمال الملف',
  'smartProfile.score.employability': 'درجة القابلية للتوظيف',
  'smartProfile.score.employabilityReserved': 'درجة القابلية للتوظيف — محجوزة لمرحلة لاحقة',
  'smartProfile.score.version': 'الإصدار 1 · محسوبة من ملفك وسيرتك',
  'smartProfile.score.computing': 'يتم حساب درجة القابلية للتوظيف…',
  'smartProfile.breakdown.title': 'تفصيل الدرجة',
  'smartProfile.breakdown.note': 'كيف يتم احتساب درجة القابلية للتوظيف.',
  'smartProfile.breakdown.cvHint': 'حلّل سيرة ذاتية لتضمين جاهزية السيرة.',
  'smartProfile.factor.completeness': 'اكتمال الملف',
  'smartProfile.factor.skills': 'عمق المهارات',
  'smartProfile.factor.experience': 'الخبرة',
  'smartProfile.factor.education': 'التعليم والمشاريع',
  'smartProfile.factor.cv': 'جاهزية السيرة',
  'smartProfile.trend.title': 'الاتجاه',
  'smartProfile.trend.first': 'تم تسجيل أول درجة',
  'smartProfile.trend.sinceLast': 'منذ آخر تحديث',
  'smartProfile.tips.title': 'ارفع درجتك',
  'smartProfile.tips.allGood': 'أداء قوي في كل الجوانب — حافظ على تحديث ملفك.',
  'smartProfile.tips.cta': 'تحديث ملفي',
  'smartProfile.tips.completeness': 'أكمل الأقسام الناقصة في ملفك لرفع نسبة الاكتمال.',
  'smartProfile.tips.skills': 'أضف مزيدًا من المهارات وحدّد مستوياتها — المهارات الموثّقة لها وزن أكبر.',
  'smartProfile.tips.experience': 'أضف خبرتك العملية وإجمالي سنوات الخبرة.',
  'smartProfile.tips.education': 'أضف التعليم أو المشاريع لاستكمال ملفك.',
  'smartProfile.tips.cv': 'ارفع سيرة ذاتية وحلّلها لإضافة جاهزية السيرة إلى درجتك.',
  'smartProfile.gap.title': 'تحليل فجوة المهارات',
  'smartProfile.gap.note': 'مهارات الدور المنسّقة مقابل ملفك.',
  'smartProfile.gap.roleLabel': 'الدور المستهدف',
  'smartProfile.gap.coverage': 'تغطية الدور',
  'smartProfile.gap.missing': 'مهارات لتعلّمها',
  'smartProfile.gap.allMatched': 'أنت تغطي كل مهارات هذا الدور.',
  'smartProfile.matches.title': 'أفضل الفرص المطابقة',
  'smartProfile.matches.empty': 'لا توجد وظائف منشورة تطابق مهاراتك بعد.',
  'smartProfile.matches.missing': 'ناقص',
  'smartProfile.recommendation.title': 'توصية الذكاء الاصطناعي',
  'smartProfile.recommendation.refresh': 'إعادة توليد التوصية',
  'smartProfile.recommendation.sourceAi': 'ذكاء اصطناعي',
  'smartProfile.recommendation.sourceRuleBased': 'قائمة على القواعد',
  'smartProfile.recommendation.suggestedRole': 'الدور المقترح',
  'smartProfile.recommendation.focus': 'مهارات للتركيز عليها',
  'smartProfile.quickStats.title': 'إحصاءات سريعة',
  'smartProfile.quickStats.skills': 'المهارات',
  'smartProfile.quickStats.experience': 'الخبرة',
  'smartProfile.quickStats.education': 'التعليم',
  'smartProfile.quickStats.projects': 'المشاريع',
  'smartProfile.skillMap.title': 'خريطة المهارات',
  'smartProfile.skillMap.note': 'مستويات مُدخلة ذاتيًا من ملفك.',
  'smartProfile.skillMap.empty': 'لا توجد مهارات مضافة بعد.',
  'smartProfile.skillMap.levelUnset': 'المستوى غير محدد',
  'smartProfile.experience.title': 'الخط الزمني للخبرة',
  'smartProfile.experience.empty': 'لا توجد خبرة مضافة بعد.',
  'smartProfile.experience.current': 'حتى الآن',
  'smartProfile.education.title': 'التعليم',
  'smartProfile.education.empty': 'لا يوجد تعليم مضاف بعد.',
  'smartProfile.cvSignals.title': 'إشارات السيرة',
  'smartProfile.cvSignals.ats': 'جاهزية ATS',
  'smartProfile.cvSignals.detected': 'المهارات المكتشفة',
  'smartProfile.reserved.label': 'محجوز',
  'smartProfile.reserved.note': 'يُفعّل في مرحلة معتمدة لاحقًا.',
  'smartProfile.reserved.recommendation.title': 'توصية الذكاء الاصطناعي',
  'smartProfile.reserved.recommendation.body':
    'إرشادات مخصصة للدور التالي والنمو عند إطلاق محرك ذكاء المسار المهني.',
  'smartProfile.reserved.gap.title': 'تحليل فجوة المهارات',
  'smartProfile.reserved.gap.body':
    'تحليل الفجوة مقارنةً بالدور المستهدف عند توفّر خريطة المهارات والأدوار المستهدفة.',
  'smartProfile.reserved.matches.title': 'أفضل الفرص المطابقة',
  'smartProfile.reserved.matches.body':
    'مطابقات أدوار قابلة للتفسير عند تشغيل محرك المطابقة بالذكاء الاصطناعي وشبكة المواهب.',
  'smartProfile.avatar.alt': 'الصورة الشخصية',
  'smartProfile.avatar.change': 'تغيير الصورة',
  'smartProfile.avatar.remove': 'إزالة الصورة',
  'smartProfile.editProfile': 'تعديل الملف الشخصي',
  'smartProfile.image.uploading': 'جارٍ الرفع…',
  'smartProfile.image.invalidType': 'يُسمح فقط بصور PNG و JPEG و WEBP.',
  'smartProfile.image.tooLarge': 'يجب ألا يتجاوز حجم الصورة 5 ميجابايت.',
  'smartProfile.image.uploadError': 'تعذّر رفع الصورة.',

  // Tier B — lightweight placeholders
  'preview.company.title': 'الشركة',
  'preview.company.body': '',
  'preview.settings.title': 'الإعدادات',
  'preview.settings.body': '',

  // Settings — Privacy & discoverability (real)
  'settings.title': 'الإعدادات',
  'settings.subtitle': 'تحكّم في خصوصيتك وما يمكن لأصحاب العمل رؤيته.',
  'settings.loading': 'جارٍ تحميل الإعدادات…',
  'settings.saveError': 'تعذّر حفظ الإعداد.',
  'settings.privacy.title': 'الخصوصية وإمكانية الاكتشاف',
  'settings.privacy.note': 'أنت تتحكم بما يراه أصحاب العمل. الخصوصية أولًا افتراضيًا.',
  'settings.privacy.reservedNote':
    'الواجهات الموجّهة لأصحاب العمل التي تستخدم هذه الإعدادات ليست مفعّلة بعد — يتم حفظ اختياراتك وتُطبَّق عند إطلاقها.',
  'settings.privacy.discoverable.label': 'قابل للاكتشاف من أصحاب العمل',
  'settings.privacy.discoverable.body': 'اسمح لأصحاب العمل بإيجاد ملفك في بحث المواهب.',
  'settings.privacy.showEmployabilityScore.label': 'إظهار درجة القابلية للتوظيف',
  'settings.privacy.showEmployabilityScore.body':
    'اسمح لأصحاب العمل برؤية درجة قابليتك للتوظيف.',
  'settings.privacy.shareContactDetails.label': 'مشاركة بيانات التواصل',
  'settings.privacy.shareContactDetails.body':
    'شارك بريدك الإلكتروني وهاتفك مع أصحاب العمل المطابقين.',
  'settings.privacy.allowRecruiterMessages.label': 'السماح برسائل الموظِّفين',
  'settings.privacy.allowRecruiterMessages.body': 'اسمح للموظِّفين بإرسال رسائل إليك.',

  // Company — job postings (employer Hiring OS)
  'company.title': 'الوظائف',
  'company.subtitle': 'أنشئ وأدر إعلانات وظائفك.',
  'company.loading': 'جارٍ تحميل الوظائف…',
  // Company sub-tabs + hiring pages
  'company.tab.jobs': 'الوظائف',
  'company.tab.applications': 'الطلبات',
  'company.tab.candidates': 'المرشحون',
  'company.tab.profile': 'ملف الشركة',
  'company.applications.title': 'الطلبات',
  'company.applications.loading': 'جارٍ تحميل الطلبات…',
  'company.applications.error': 'تعذّر تحميل الطلبات.',
  'company.applications.empty': 'لا توجد طلبات بعد.',
  'company.applications.col.candidate': 'المرشح',
  'company.applications.col.job': 'الوظيفة',
  'company.applications.col.status': 'الحالة',
  'company.candidates.title': 'البحث عن مرشحين',
  'company.candidates.search': 'بحث',
  'company.candidates.searchPlaceholder': 'ابحث في العنوان أو الملخص أو المهارة',
  'company.candidates.skillsPlaceholder': 'المهارات (مفصولة بفواصل)',
  'company.candidates.privacyNote':
    'يظهر هنا فقط المرشحون الذين فعّلوا الظهور.',
  'company.candidates.empty': 'لا يوجد مرشحون مطابقون.',
  'company.candidates.makeOffer': 'تقديم عرض',
  'company.candidates.offerTitle': 'المسمى / الدور',
  'company.candidates.offerComp': 'التعويض',
  'company.candidates.sendOffer': 'إرسال العرض',
  'company.candidates.offerSent': 'تم إرسال العرض',
  'company.candidates.offerError': 'تعذّر إرسال العرض.',
  'company.profile.title': 'ملف الشركة',
  'company.profile.loading': 'جارٍ التحميل…',
  'company.profile.error': 'تعذّر تحميل ملف الشركة.',
  'company.profile.saved': 'تم حفظ ملف الشركة.',
  'company.profile.saveError': 'تعذّر حفظ ملف الشركة.',
  'company.profile.name': 'اسم الشركة',
  'company.profile.about': 'نبذة',
  'company.profile.website': 'الموقع الإلكتروني',
  'company.profile.industry': 'المجال',
  'company.profile.size': 'حجم الشركة',
  'company.profile.location': 'الموقع',
  'company.profile.save': 'حفظ الملف',
  'company.empty': 'لا توجد وظائف بعد — أنشئ أول إعلان أعلاه.',
  'company.form.title': 'إعلان وظيفة جديد',
  'company.form.jobTitle': 'المسمى الوظيفي',
  'company.form.location': 'الموقع',
  'company.form.employmentType': 'نوع التوظيف',
  'company.form.workMode': 'نمط العمل',
  'company.form.skills': 'المهارات المطلوبة (مفصولة بفواصل)',
  'company.form.description': 'الوصف',
  'company.form.submit': 'إنشاء وظيفة',
  'company.form.titleRequired': 'يلزم مسمى وظيفي من حرفين على الأقل.',
  'company.createError': 'تعذّر إنشاء الوظيفة.',
  'company.actionError': 'تعذّر تحديث الوظيفة.',
  'company.list.title': 'وظائفك',
  'company.action.publish': 'نشر',
  'company.action.close': 'إغلاق',
  'company.action.delete': 'حذف',
  'company.employmentType.full_time': 'دوام كامل',
  'company.employmentType.part_time': 'دوام جزئي',
  'company.employmentType.contract': 'عقد',
  'company.employmentType.internship': 'تدريب',
  'company.employmentType.temporary': 'مؤقت',
  'company.workMode.onsite': 'في الموقع',
  'company.workMode.remote': 'عن بُعد',
  'company.workMode.hybrid': 'هجين',
  'company.status.draft': 'مسودة',
  'company.status.published': 'منشورة',
  'company.status.closed': 'مغلقة',

  // Company — extended jobs (professional posting)
  'company.newJob': 'وظيفة جديدة',
  'company.empty.body': 'أنشئ أول إعلان وظيفة لتبدأ في استقبال الطلبات.',
  'company.form.new': 'إعلان وظيفة جديد',
  'company.form.edit': 'تعديل إعلان الوظيفة',
  'company.form.update': 'حفظ التغييرات',
  'company.form.saved': 'تم حفظ الوظيفة.',
  'company.form.department': 'القسم',
  'company.form.experienceLevel': 'مستوى الخبرة',
  'company.form.educationLevel': 'المستوى التعليمي',
  'company.form.salaryMin': 'الراتب (الحد الأدنى)',
  'company.form.salaryMax': 'الراتب (الحد الأقصى)',
  'company.form.currency': 'العملة',
  'company.form.salaryPeriod': 'فترة الدفع',
  'company.form.openings': 'عدد الشواغر',
  'company.form.deadline': 'آخر موعد للتقديم',
  'company.form.visaSponsorship': 'كفالة التأشيرة',
  'company.form.visaSponsorship.yes': 'نوفّر كفالة التأشيرة',
  'company.form.responsibilities': 'المسؤوليات',
  'company.form.requirements': 'المتطلبات',
  'company.form.benefits': 'المزايا والمكافآت',
  'company.action.edit': 'تعديل',
  'company.action.reopen': 'إعادة فتح',
  'company.experienceLevel.intern': 'متدرب',
  'company.experienceLevel.entry': 'مبتدئ',
  'company.experienceLevel.mid': 'متوسط',
  'company.experienceLevel.senior': 'خبير',
  'company.experienceLevel.lead': 'قائد / رئيسي',
  'company.experienceLevel.executive': 'تنفيذي',
  'company.educationLevel.none': 'بدون اشتراط',
  'company.educationLevel.high_school': 'الثانوية',
  'company.educationLevel.associate': 'دبلوم',
  'company.educationLevel.bachelor': 'بكالوريوس',
  'company.educationLevel.master': 'ماجستير',
  'company.educationLevel.doctorate': 'دكتوراه',
  'company.salaryPeriod.hour': 'ساعة',
  'company.salaryPeriod.day': 'يوم',
  'company.salaryPeriod.month': 'شهر',
  'company.salaryPeriod.year': 'سنة',
  'company.job.openings': 'شواغر',
  'company.job.visa': 'كفالة تأشيرة',
  'company.delete.confirm': 'حذف إعلان الوظيفة هذا؟',

  // Company — applications (filter + actions)
  'company.applications.col.applied': 'تاريخ التقديم',
  'company.applications.updated': 'تم تحديث الطلب.',
  'company.applications.status.new': 'جديد',
  'company.applications.status.reviewing': 'قيد المراجعة',
  'company.applications.status.offered': 'تم العرض',
  'company.applications.status.accepted': 'مقبول',
  'company.applications.status.rejected': 'مرفوض',
  'company.applications.status.withdrawn': 'مسحوب',
  'company.applications.action.review': 'وضع قيد المراجعة',
  'company.applications.action.offer': 'وضع كعرض',
  'company.applications.action.accept': 'قبول',
  'company.applications.action.reject': 'رفض',

  // Company — find candidates
  'company.candidates.clear': 'مسح',
  'company.candidates.complete': 'اكتمال الملف',
  'company.candidates.contactPrivate': 'بيانات التواصل خاصة',
  'company.candidates.searchResults': 'نتائج البحث',
  'company.candidates.topResults': 'أبرز المرشحين',
  'company.candidates.yearsExp': 'سنوات خبرة',
  'company.candidates.years': 'سنة',
  'gender.male': 'ذكر',
  'gender.female': 'أنثى',

  // Company — profile (rich fields)
  'company.profile.tagline': 'الشعار',
  'company.profile.tagline.placeholder': 'ما تشتهر به شركتك',
  'company.profile.mission': 'الرسالة',
  'company.profile.culture': 'الثقافة',
  'company.profile.founded': 'سنة التأسيس',
  'company.profile.contactEmail': 'البريد الإلكتروني للتواصل',
  'company.profile.contactPhone': 'هاتف التواصل',
  'company.profile.linkedin': 'لينكدإن',
  'company.profile.twitter': 'إكس / تويتر',
  'company.profile.github': 'جيت‌هَب',
  'company.profile.benefits': 'المزايا والمكافآت',
  'company.profile.benefits.placeholder': 'تأمين صحي، عمل عن بُعد…',
  'company.profile.techStack': 'التقنيات المستخدمة',
  'company.profile.values': 'القيم',
  'company.profile.values.placeholder': 'الملكية، الشفافية…',
  'company.profile.completeness': 'اكتمال الملف',
  'company.profile.section.story': 'قصة الشركة',
  'company.profile.section.facts': 'معلومات الشركة',
  'company.profile.section.contact': 'التواصل ووسائل التواصل',
  'company.profile.section.perks': 'المزايا والتقنيات والقيم',
  'company.profile.size.any': 'اختر الحجم',

  // Employer dashboard (redesigned)
  'employerDash.loading': 'جارٍ تحميل لوحة التحكم…',
  'employerDash.kpi.jobs': 'إجمالي الوظائف',
  'employerDash.kpi.published': 'المنشورة',
  'employerDash.kpi.applications': 'الطلبات',
  'employerDash.kpi.review': 'قيد المراجعة',
  'employerDash.kpi.offers': 'العروض',
  'employerDash.kpi.hired': 'تم التوظيف',
  'employerDash.chart.applications': 'مسار الطلبات',
  'employerDash.chart.jobs': 'الوظائف حسب الحالة',
  'employerDash.funnel.title': 'قمع التوظيف',
  'employerDash.funnel.totalApplications': 'إجمالي الطلبات',
  'employerDash.funnel.applications': 'الطلبات',
  'employerDash.funnel.offers': 'العروض',
  'employerDash.funnel.hires': 'التعيينات',
  'employerDash.funnel.offerRate': 'نسبة العروض',
  'employerDash.funnel.hireRate': 'نسبة التوظيف',

  // Admin — early-access requests
  'adminEarlyAccess.title': 'طلبات الوصول المبكر',
  'adminEarlyAccess.subtitle': 'راجع تسجيلات قائمة الانتظار وحدّث حالتها.',
  'adminEarlyAccess.loading': 'جارٍ تحميل الطلبات…',
  'adminEarlyAccess.empty': 'لا توجد طلبات وصول مبكر بعد.',
  'adminEarlyAccess.updateError': 'تعذّر تحديث الطلب.',
  'adminEarlyAccess.col.name': 'الاسم',
  'adminEarlyAccess.col.email': 'البريد الإلكتروني',
  'adminEarlyAccess.col.interestedAs': 'مهتم بصفة',
  'adminEarlyAccess.col.status': 'الحالة',
  'adminEarlyAccess.status.new': 'جديد',
  'adminEarlyAccess.status.reviewed': 'تمت المراجعة',
  'adminEarlyAccess.status.invited': 'مدعو',
  'adminEarlyAccess.status.approved': 'مقبول',
  'adminEarlyAccess.status.rejected': 'مرفوض',
  'adminEarlyAccess.finalized': 'منتهٍ',
  'adminEarlyAccess.approve': 'قبول',
  'adminEarlyAccess.reject': 'رفض',
  'adminEarlyAccess.updated': 'تم تحديث الطلب.',
  'preview.analytics.title': 'التحليلات',
  'preview.analytics.body': '',
  'preview.integrations.title': 'التكاملات',
  'preview.integrations.body': '',

} as const;

export const ar: Messages = {
  ...appLevel,
  ...chrome,
  ...landingAr,
  ...landingPlaceholdersAr,
  ...authAr,
  ...earlyAccessAr,
};
